import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import WeekendIcon from '@mui/icons-material/Weekend';
import { describeCorte, describeCreditTerm, describeModificadores, describeVencimiento } from '../../helpers/describe';
import { ICreditTerm } from '../../types';

interface CreditTermSummaryProps {
	term: ICreditTerm;
	/** `line`: una sola línea (chips, tablas). `full`: los dos ejes + modificadores. */
	variant?: 'line' | 'full';
}

/**
 * Muestra una condición de crédito en español legible. Es el componente que
 * unifica cómo se ve una condición en TODAS las pantallas: la ficha del
 * cliente, el listado de gestión y el drawer de facturación.
 *
 * Nunca se muestran los parámetros crudos: quien lee la pantalla no tiene por
 * qué saber que existe un `vencOffsetMeses`.
 */
export function CreditTermSummary({ term, variant = 'full' }: CreditTermSummaryProps): JSX.Element {
	if (variant === 'line') {
		return (
			<Typography variant="body2" color="text.secondary">
				{describeCreditTerm(term)}
			</Typography>
		);
	}

	const corte = describeCorte(term);
	const modificadores = describeModificadores(term);

	return (
		<Stack spacing={1}>
			{/* Los dos ejes, separados para que se lean como lo que son. */}
			<Box display="flex" alignItems="flex-start" gap={1}>
				<EventRepeatIcon sx={{ fontSize: 18, color: 'text.disabled', mt: 0.25 }} />
				<Box>
					<Typography variant="caption" color="text.secondary" display="block">
						Cuándo se factura
					</Typography>
					<Typography variant="body2" fontWeight={500}>
						{corte ?? 'Al emitir cada servicio (sin corte)'}
					</Typography>
				</Box>
			</Box>

			<Box display="flex" alignItems="flex-start" gap={1}>
				<Box sx={{ width: 18, display: 'flex', justifyContent: 'center', mt: 0.25 }}>
					<Typography variant="caption" color="text.disabled" fontWeight={700}>
						$
					</Typography>
				</Box>
				<Box>
					<Typography variant="caption" color="text.secondary" display="block">
						Cuándo vence
					</Typography>
					<Typography variant="body2" fontWeight={500}>
						{describeVencimiento(term)}
					</Typography>
				</Box>
			</Box>

			{modificadores.length > 0 && (
				<Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5, pl: 3.5 }}>
					{modificadores.map((m) => (
						<Chip
							key={m}
							icon={
								m.includes('viaje') ? (
									<FlightTakeoffIcon sx={{ fontSize: 13 }} />
								) : (
									<WeekendIcon sx={{ fontSize: 13 }} />
								)
							}
							label={m}
							size="small"
							variant="outlined"
							sx={{ fontSize: 11 }}
						/>
					))}
				</Stack>
			)}
		</Stack>
	);
}

/** Chip compacto con la condición; el detalle completo va en el tooltip. */
export function CreditTermChip({ term }: { term: ICreditTerm }): JSX.Element {
	return (
		<Tooltip
			title={
				<Box sx={{ p: 0.5 }}>
					<Typography variant="body2" fontWeight={600}>
						{term.name}
					</Typography>
					<Typography variant="caption">{describeCreditTerm(term)}</Typography>
					{describeModificadores(term).map((m) => (
						<Typography key={m} variant="caption" display="block" sx={{ opacity: 0.85 }}>
							· {m}
						</Typography>
					))}
				</Box>
			}
			arrow
			placement="top"
		>
			<Chip label={term.name} size="small" color="primary" variant="outlined" sx={{ cursor: 'help' }} />
		</Tooltip>
	);
}
